import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntitlement } from '@/hooks/useEntitlement';
import { useGatingStore } from '@/stores/gating.store';
import { getPlanDisplayName } from '@/components/gating/gatingCopy';
import type { EntitlementFeatureKey } from '@/types/package';

/**
 * For controls that stay visible but are locked (Brand World actions, auto-translate toggle, role changes):
 * `locked`, a ready "Available on {Plan}" label for tooltips, and `guard(fn)` which runs `fn` when allowed and
 * opens the upsell modal when locked (an explicit click always opens it).
 */
export const useFeatureLock = (feature: EntitlementFeatureKey) => {
  const { t } = useTranslation('common');
  const { locked, upsellPlanSlug } = useEntitlement(feature);
  const learnedSlug = useGatingStore((s) => s.lockedSlugs[feature] ?? null);
  const openUpsell = useGatingStore((s) => s.openUpsell);

  const slug = upsellPlanSlug ?? learnedSlug;
  const planName = getPlanDisplayName(slug);
  const label = planName ? t('gating_available_on', { plan: planName }) : t('gating_available_on_generic');

  const showUpsell = useCallback(() => {
    openUpsell({ feature, upsellPlanSlug: slug }, true);
  }, [openUpsell, feature, slug]);

  const guard = useCallback(
    <Args extends unknown[]>(fn: (...args: Args) => void) =>
      (...args: Args): void => {
        if (locked) {
          showUpsell();
          return;
        }
        fn(...args);
      },
    [locked, showUpsell]
  );

  return { locked, label, showUpsell, guard, upsellPlanSlug: slug };
};
